import { ComboboxItem } from "@mantine/core"
import { useThrottledCallback } from "@mantine/hooks"
import { DefaultSingleSelectProps } from "../DefaultSingleSelect"
import { useDefaultSuspenseInfiniteQuery } from "../../utils/useDefaultSuspenseInfiniteQuery"
import { Pagination } from "../../utils/Pagination"
import { DefaultSingleSelect } from "../DefaultSingleSelect"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"

type ExtractType<TData> = TData extends Pagination<infer T> ? T : never

export interface SingleSelectFromInfiniteQueryProps<
  TQueryData extends ReturnType<
    typeof useDefaultSuspenseInfiniteQuery | typeof useDefaultInfiniteQuery
  >,
  TData = TQueryData extends ReturnType<
    | typeof useDefaultSuspenseInfiniteQuery<
        any,
        infer T extends Pagination<any>
      >
    | typeof useDefaultInfiniteQuery<any, infer T extends Pagination<any>>
  >
    ? ExtractType<T>
    : unknown,
> extends Omit<
    DefaultSingleSelectProps,
    "options" | "searchValue" | "onSearchChange"
  > {
  queryData: TQueryData
  entitiesToComboboxData: (data: TData) => ComboboxItem
  searchValue?: string
  onSearchChange?: (val: string) => void
  loading?: boolean
}

export function SingleSelectFromInfiniteQuery<
  TQueryData extends ReturnType<
    typeof useDefaultSuspenseInfiniteQuery | typeof useDefaultInfiniteQuery
  >,
  TData = TQueryData extends ReturnType<
    | typeof useDefaultSuspenseInfiniteQuery<
        any,
        infer T extends Pagination<any>
      >
    | typeof useDefaultInfiniteQuery<any, infer T extends Pagination<any>>
  >
    ? ExtractType<T>
    : unknown,
>({
  searchValue,
  onSearchChange,
  entitiesToComboboxData,
  loading,
  queryData,
  ...rest
}: SingleSelectFromInfiniteQueryProps<TQueryData, TData>) {
  const [internalSearchValue, setInternalSearchValue] = React.useState(
    searchValue ?? ""
  )

  React.useEffect(() => {
    setInternalSearchValue(searchValue ?? "")
  }, [searchValue])

  const { data, hasNextPage, isFetchingNextPage, isLoading, fetchNextPage } =
    queryData

  const throttledFetchNextPage = useThrottledCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, 300)

  const handleSearchValueChange = (newVal: string) => {
    onSearchChange?.(newVal)
    setInternalSearchValue(newVal)
  }

  return (
    <DefaultSingleSelect
      searchValue={internalSearchValue}
      onSearchChange={handleSearchValueChange}
      options={
        data?.pages.flatMap((page) =>
          page?.data.flatMap((entity) => entitiesToComboboxData(entity))
        ) ?? []
      }
      value={rest.value}
      loading={isLoading || loading}
      onScrolledToBottom={throttledFetchNextPage}
      {...rest}
    />
  )
}
